import { createAdminRequest, type AdminHttpOptions } from "../../../lib/adminHttp";
import { M1ApiError } from "../../m1-app/data/M1AppApi";
import {
  loginPolicyAppIdSchema, loginPolicyAuditPageSchema, loginPolicyListQuerySchema, loginPolicyPageSchema,
  loginPolicyPaginationSchema, loginPolicyPublishSchema,
  loginPolicyRevisionsPageSchema, loginPolicyRollbackSchema, loginPolicySchema,
  loginPolicyUpdateSchema, type LoginPolicy, type LoginPolicyAuditPage,
  type LoginPolicyListQuery, type LoginPolicyPage, type LoginPolicyPagination,
  type LoginPolicyPublishInput, type LoginPolicyRevisionsPage, type LoginPolicyRollbackInput,
  type LoginPolicyUpdateInput,
} from "@yahshi/contracts";

export interface LoginPolicyApi {
  list(query?: Partial<LoginPolicyListQuery>): Promise<LoginPolicyPage>;
  get(appId: string): Promise<LoginPolicy>;
  update(appId: string, input: LoginPolicyUpdateInput): Promise<LoginPolicy>;
  publish(appId: string, input: LoginPolicyPublishInput): Promise<LoginPolicy>;
  rollback(appId: string, input: LoginPolicyRollbackInput): Promise<LoginPolicy>;
  revisions(appId: string, query?: Partial<LoginPolicyPagination>): Promise<LoginPolicyRevisionsPage>;
  audit(appId: string, query?: Partial<LoginPolicyPagination>): Promise<LoginPolicyAuditPage>;
}

const base = "/admin/settings/login-policies";
const path = (appId: string) => `${base}/${encodeURIComponent(loginPolicyAppIdSchema.parse(appId))}`;
const queryString = (value: object) => new URLSearchParams(Object.entries(value).map(([key, item]) => [key, String(item)])).toString();
const post = (value: unknown) => ({ method: "POST", body: JSON.stringify(value) });

export function createHttpLoginPolicyApi(options: AdminHttpOptions): LoginPolicyApi {
  const request = createAdminRequest(options);
  const requestPolicy = (appId: string, suffix = "", init?: RequestInit) => {
    const id = loginPolicyAppIdSchema.parse(appId);
    return request(`${path(id)}${suffix}`, loginPolicySchema, init).then(policy => {
      if (policy.appId !== id) throw new M1ApiError("INVALID_RESPONSE", "返回的策略不属于请求应用，请重新加载后重试");
      return policy;
    });
  };
  return {
    list: (query = {}) => request(`${base}?${queryString(loginPolicyListQuerySchema.parse(query))}`, loginPolicyPageSchema),
    get: (appId) => requestPolicy(appId),
    update: (appId, input) => requestPolicy(appId, "", { method: "PATCH", body: JSON.stringify(loginPolicyUpdateSchema.parse(input)) }),
    publish: (appId, input) => requestPolicy(appId, "/publish", post(loginPolicyPublishSchema.parse(input))),
    rollback: (appId, input) => requestPolicy(appId, "/rollback", post(loginPolicyRollbackSchema.parse(input))),
    revisions: (appId, query = {}) => request(`${path(appId)}/revisions?${queryString(loginPolicyPaginationSchema.parse(query))}`, loginPolicyRevisionsPageSchema),
    audit: (appId, query = {}) => request(`${path(appId)}/audit?${queryString(loginPolicyPaginationSchema.parse(query))}`, loginPolicyAuditPageSchema),
  };
}
